import { Phone, Facebook, Instagram, Car, Bus, Accessibility, Clock } from "lucide-react";
import { CHURCH } from "@/data/church";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { GoldDivider } from "@/components/GoldDivider";

const ContactCards = () => (
  <section data-testid="contact-cards-section" className="bg-gradient-to-b from-[#F7ECD9] via-[#F3E8FA] to-[#FBF6EC] py-16 sm:py-24">
    <div className="mx-auto grid max-w-5xl gap-8 px-5 sm:px-8 md:grid-cols-2">
      <Reveal>
        <div className="flex h-full flex-col rounded-3xl border-2 border-[#C59B4B]/60 bg-[#6B21A8] p-8 shadow-lg sm:p-10">
          <Phone className="h-9 w-9 text-[#F2C14E]" aria-hidden="true" />
          <h2 className="mt-5 font-serif text-xl sm:text-2xl font-medium text-[#FBF6EC]">
            Call Us
          </h2>
          <p className="mt-3 flex-1 text-base leading-relaxed text-[#F3E8FA]">
            The simplest way to reach us. Ask about services, events or your first visit —
            a friendly voice will answer.
          </p>
          <a
            href={CHURCH.phoneHref}
            data-testid="contact-call-button"
            className="mt-6 inline-flex min-h-14 items-center justify-center gap-3 rounded-xl bg-[#C59B4B] px-6 text-lg font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] hover:shadow-lg active:scale-95"
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
            {CHURCH.phoneDisplay}
          </a>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="flex h-full flex-col rounded-3xl border-2 border-[#C59B4B]/60 bg-[#0F766E] p-8 shadow-lg sm:p-10">
          <Facebook className="h-9 w-9 text-[#F2C14E]" aria-hidden="true" />
          <h2 className="mt-5 font-serif text-xl sm:text-2xl font-medium text-[#FBF6EC]">
            Follow Us
          </h2>
          <p className="mt-3 flex-1 text-base leading-relaxed text-[#E9F2EC]">
            Event posters, visiting mediums and reminders are shared on our social pages
            each month.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <a
              href={CHURCH.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-button"
              className="inline-flex min-h-14 items-center justify-center gap-3 rounded-xl bg-[#C59B4B] px-6 text-lg font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] active:scale-95"
            >
              <Facebook className="h-5 w-5" aria-hidden="true" />
              Facebook
            </a>
            <a
              href={CHURCH.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-button"
              className="inline-flex min-h-14 items-center justify-center gap-3 rounded-xl border-2 border-[#FBF6EC]/50 px-6 text-lg font-semibold text-[#FBF6EC] transition-all duration-200 hover:border-[#F2C14E] hover:text-[#F2C14E] active:scale-95"
            >
              <Instagram className="h-5 w-5" aria-hidden="true" />
              Instagram
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const GETTING_HERE = [
  {
    icon: Car,
    title: "By Car",
    text: "Free car park.",
  },
  {
    icon: Bus,
    title: "By Bus & Train",
    text: "Co-operative Street is a short, level walk from Stafford town centre and the main bus routes. Stafford railway station is around ten minutes on foot.",
  },
  {
    icon: Accessibility,
    title: "Accessibility",
    text: "The hall is on one level with step-free access at the entrance. If you would like any help on arrival, please phone ahead and we will look out for you.",
  },
];

const CHIP_COLORS = ["bg-[#6B21A8]", "bg-[#0F766E]", "bg-[#A61C4E]"];

const GettingHere = () => (
  <section data-testid="getting-here-section" className="bg-[#D8E6CC] py-16 sm:py-24">
    <div className="mx-auto max-w-3xl px-5 sm:px-8">
      <Reveal>
        <h2 className="text-center font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#6B21A8]">
          Getting Here
        </h2>
      </Reveal>
      <div className="mt-10 flex flex-col gap-7">
        {GETTING_HERE.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.1}>
            <div data-testid={`getting-here-${i}`} className="flex gap-5">
              <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${CHIP_COLORS[i]} shadow-md`}>
                <g.icon className="h-6 w-6 text-[#F2C14E]" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-medium text-[#101826]">{g.title}</h3>
                <p className="mt-1.5 text-base leading-relaxed text-[#3D4958]">{g.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ServiceReminder = () => (
  <section data-testid="contact-service-section" className="bg-gradient-to-b from-[#F3E8FA] to-[#FBF6EC] py-16 sm:py-24">
    <div className="mx-auto max-w-3xl px-5 sm:px-8">
      <Reveal>
        <div className="rounded-3xl border-2 border-[#C59B4B] bg-gradient-to-b from-[#FBEEDB] to-[#F7ECD9] p-8 text-center shadow-lg sm:p-12">
          <Clock className="mx-auto h-8 w-8 text-[#A61C4E]" aria-hidden="true" />
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl font-medium text-[#6B21A8]">
            Sunday Divine Service
          </h2>
          <p className="mt-4 font-serif text-4xl sm:text-5xl font-semibold text-[#101826]">
            Every Sunday · 6:30pm
          </p>
          <GoldDivider className="mx-auto my-6 max-w-xs" />
          <p className="text-base sm:text-lg text-[#3D4958]">
            Healing is available before all Sunday services
          </p>
          <p className="mt-4 text-base sm:text-lg text-[#3D4958]">
            There is no need to book — simply come along, and you will be warmly welcomed.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Contact() {
  return (
    <>
      <PageHero
        testId="contact-hero"
        kicker="Contact & Directions"
        title="We Would Love to Hear From You"
        lead="One phone call is all it takes — or simply come along on a Sunday evening. No forms, no fuss."
      />
      <ContactCards />
      <GettingHere />
      <ServiceReminder />
    </>
  );
}
